import React, { useMemo } from "react";
import { ThemeProvider } from "@emotion/react";
import { CssBaseline, createTheme } from "@mui/material";
import ComponentsOverrides from "./theme";
import { Route, Router } from "./utils/Router";
import OtpForm from "./components/OtpForm";
import { FormDataProvider } from "./utils/formDataProvider";
import { ResponseContextProvider } from "./utils/itemDataProvider";
import { SnackbarProvider } from 'notistack';
import { ReturnContextProvider } from "./utils/returnDataProvider";
import OrderDetailForm from "./components/OrderDetailForm";
import ItemList from "./components/ItemList";
import { ConfigContextProvider } from "./utils/configDataProvider";
import { DomainConfigProvider, useDomainConfig } from "./utils/domainConfigProvider";
import CustomOrderDetailForm from "./components/CustomPages/CustomLoginPage";
import AppConfig from "./utils/config";

function App() {
  return (
    // DomainConfigProvider must wrap the theme so it can be branded per brand URL.
    <FormDataProvider>
      <DomainConfigProvider>
        <ThemedRoot />
      </DomainConfigProvider>
    </FormDataProvider>
  );
}

function ThemedRoot() {
  const { domainConfig } = useDomainConfig();
  const isNuvah = domainConfig?.brandUrl === AppConfig?.CustomeUI?.NuvahURL;

  // Nuvah gets the brand color as the MUI primary, so every primary-derived
  // "blue" (buttons, focused inputs, links, primary icons) becomes brand color.
  // All other brands keep the default MUI palette.
  const theme = useMemo(() => {
    const t = createTheme({
      palette: {
        background: {
          default: '#f5f5f5',
        },
        ...(isNuvah && {
          primary: {
            main: AppConfig.CustomeUI.NuvahBrandColor,
          },
        }),
      },
      typography: {
        fontFamily: ["Poppins", "sans-serif"].join(","),
      },
    });
    t.components = ComponentsOverrides(t);
    return t;
  }, [isNuvah]);

  return (
    <ThemeProvider theme={theme}>
      <SnackbarProvider autoHideDuration={5000} maxSnack={3} anchorOrigin={{
        vertical: "top",
        horizontal: "center",
      }}>
        <CssBaseline />
        <ConfigContextProvider>
          <AppContent />
        </ConfigContextProvider>
      </SnackbarProvider>
    </ThemeProvider>
  );
}

function AppContent() {
  const { domainConfig, loading } = useDomainConfig();
  const isTestDomain = domainConfig?.brandUrl === AppConfig?.CustomeUI?.BrandURL;

  if (loading) {
    return null;
  }

  return (
    <ResponseContextProvider>
      <ReturnContextProvider>
        <Router>
          <Route
            path="/"
            index
            element={
              <React.Fragment>
                {isTestDomain ? <CustomOrderDetailForm /> : <OrderDetailForm />}
              </React.Fragment>
            }
          />
          <Route path="/items" element={<ItemList />} />
          <Route path="/otp" otp element={<OtpForm />} />
        </Router>
      </ReturnContextProvider>
    </ResponseContextProvider>
  );
}

export default App;
